import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ClockIcon } from '../../assets/icons/clock-icon.svg';
import { normalizeTimeZone } from '../../config/timezonesLabel';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import dashboardApi from '../../utils/api/dashboard';
import SelectField from '../select-field/SelectField';

const FALLBACK_TIME_ZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Jakarta',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Riyadh',
  'Australia/Sydney',
  'Pacific/Auckland',
];

/**
 * Get the current UTC offset label for an IANA time zone (e.g. "GMT+05:30")
 * @param {string} timeZone - The IANA time zone name
 * @returns {string} The formatted UTC offset label
 */
const getUtcOffsetLabel = (timeZone: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    });
    const offsetPart = formatter
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName');
    return offsetPart?.value || 'GMT';
  } catch {
    return 'GMT';
  }
};

/**
 * Build the sorted list of selectable time zone options with a formatted UTC offset label
 * @returns {{ value: string; label: string }[]} The time zone options
 */
const getTimeZoneOptions = (): { value: string; label: string }[] => {
  let zones: string[] = FALLBACK_TIME_ZONES;
  try {
    const { supportedValuesOf } = Intl as typeof Intl & {
      supportedValuesOf?: (key: string) => string[];
    };
    if (typeof supportedValuesOf === 'function') {
      zones = supportedValuesOf('timeZone');
    }
  } catch {
    zones = FALLBACK_TIME_ZONES;
  }

  // Normalize and remove duplicates
  const normalizedZones = Array.from(new Set(zones.map((zone) => normalizeTimeZone(zone))));

  return normalizedZones
    .map((zone) => ({
      value: zone,
      label: `(${getUtcOffsetLabel(zone)}) ${zone.replace(/_/g, ' ')}`,
    }))
    .sort((a, b) => a.value.localeCompare(b.value));
};

const TIME_ZONE_OPTIONS = getTimeZoneOptions();

interface TimeZoneSelectorProperties {
  initialTimeZone?: string;
}

/**
 * Time zone selector component. Loads the saved time zone from the API on mount, falling
 * back to the browser's system time zone when none is saved, and lets the user change it
 * manually from a dropdown of IANA time zones.
 * @param {TimeZoneSelectorProperties} properties - Component properties
 * @returns {any} The rendered component
 */
function TimeZoneSelector(properties: TimeZoneSelectorProperties) {
  const { initialTimeZone } = properties;
  const { t } = useTranslation('sharedComponent');
  const dispatch = useAppDispatch();
  const [timeZone, setTimeZone] = useState(initialTimeZone || '');

  useEffect(() => {
    if (initialTimeZone) {
      setTimeZone(normalizeTimeZone(initialTimeZone));
      return;
    }
    const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    dashboardApi
      .getUserTimeZone()
      .then((response) => {
        setTimeZone(normalizeTimeZone(response?.data?.timeZone || systemTimeZone));
      })
      .catch(() => {
        setTimeZone(normalizeTimeZone(systemTimeZone));
      });
  }, [initialTimeZone]);

  /**
   * Select a time zone and persist it via the API
   * @param {string} _name - The field name (unused, required by DefaultSelectField's signature)
   * @param {string} value - The selected IANA time zone name
   */
  const handleTimeZoneChange = (_name: string, value: string) => {
    setTimeZone(value);
    dashboardApi.updateUserTimeZone({ timeZone: value }).catch(() => {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('failedToUpdateTimeZone', 'Failed to update time zone.'),
          type: 'error',
        }),
      );
    });
  };

  return (
    <SelectField
      name="timeZone"
      options={TIME_ZONE_OPTIONS}
      selectedValue={timeZone}
      onChangeValue={handleTimeZoneChange}
      svgIcon={<ClockIcon />}
    />
  );
}

TimeZoneSelector.defaultProps = {
  initialTimeZone: '',
};

export default TimeZoneSelector;
